import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Header } from '../components/Header';
import { ContactLabelSelector } from '../components/ContactLabelSelector';
import { apiService } from '../services/api';
import { Category, Contact, Funnel } from '../types';

type ColumnId = string;

function buildColumnIdFromCategoriaId(categoriaId: string | null | undefined): ColumnId {
  return categoriaId || 'none';
}

function categoriaIdFromColumnId(columnId: ColumnId): string | null {
  return columnId === 'none' ? null : columnId;
}

function KanbanCard({ contact, onSendMessage, labelColors }: { contact: Contact; onSendMessage: (contact: Contact) => void; labelColors: Record<string, string> }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: contact.id });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="bg-white border border-gray-200 rounded-lg shadow-sm p-3 cursor-grab active:cursor-grabbing"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-gray-900 break-words">{contact.nome}</div>
          <div className="text-xs text-gray-600 mt-1 break-all">{contact.telefone}</div>
        </div>
        {/* Botão de enviar mensagem — stopPropagation p/ não iniciar o drag */}
        <button
          type="button"
          title="Enviar mensagem no Chat CRM"
          aria-label="Enviar mensagem"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onSendMessage(contact);
          }}
          className="shrink-0 flex h-8 w-8 items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-600 transition hover:bg-emerald-100"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        </button>
      </div>
      <div className="text-[11px] text-gray-400 mt-2 break-all">{contact.id}</div>
      {(contact.tags || []).length > 0 && <div className="mt-2 flex flex-wrap gap-1">{contact.tags.map((tag) => {
        const color = labelColors[tag.trim().toLowerCase()] || '#64748b';
        return <span key={tag} className="rounded-full border px-2 py-0.5 text-[10px] font-medium text-white" style={{ backgroundColor: color, borderColor: color }}>{tag}</span>;
      })}</div>}
    </div>
  );
}

function KanbanColumn({
  title,
  color,
  columnId,
  contactIds,
  contactsById,
  onSendMessage,
  total,
  loadingMore,
  onLoadMore,
  labelColors,
}: {
  title: string;
  color?: string | null;
  columnId: ColumnId;
  contactIds: string[];
  contactsById: Record<string, Contact>;
  onSendMessage: (contact: Contact) => void;
  total: number;
  loadingMore: boolean;
  onLoadMore: () => void;
  labelColors: Record<string, string>;
}) {
  const droppableId = `column:${columnId}`;
  const { setNodeRef, isOver } = useDroppable({
    id: droppableId,
    data: { columnId },
  });

  return (
    <div className="w-80 flex-shrink-0">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: color || '#94a3b8' }}
            />
            <div className="text-sm font-semibold text-gray-900">{title}</div>
          </div>
          <div className="text-xs text-gray-500">{total}</div>
        </div>

        <div
          ref={setNodeRef}
          onScroll={(event) => {
            const element = event.currentTarget;
            if (element.scrollHeight - element.scrollTop - element.clientHeight < 160) {
              onLoadMore();
            }
          }}
          className={`p-3 space-y-3 min-h-[120px] max-h-[calc(100vh-260px)] overflow-y-auto ${
            isOver ? 'bg-blue-50' : 'bg-slate-50'
          }`}
        >
          <SortableContext items={contactIds} strategy={verticalListSortingStrategy}>
            {contactIds.map((id) => {
              const contact = contactsById[id];
              if (!contact) return null;
              return <KanbanCard key={id} contact={contact} onSendMessage={onSendMessage} labelColors={labelColors} />;
            })}
          </SortableContext>
          {loadingMore && (
            <div className="flex items-center justify-center gap-2 py-3 text-xs text-gray-500">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
              Carregando mais contatos...
            </div>
          )}
          {!loadingMore && contactIds.length < total && (
            <button
              type="button"
              onClick={onLoadMore}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
            >
              Carregar mais
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function ContactsKanbanPage() {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<Category[]>([]);
  const [contactsById, setContactsById] = useState<Record<string, Contact>>({});
  const [columnOrder, setColumnOrder] = useState<ColumnId[]>([]);
  const [columns, setColumns] = useState<Record<ColumnId, string[]>>({});
  const [columnTotals, setColumnTotals] = useState<Record<ColumnId, number>>({});
  const [loadingMoreColumns, setLoadingMoreColumns] = useState<Record<ColumnId, boolean>>({});
  const [activeContactId, setActiveContactId] = useState<string | null>(null);
  const [funnels, setFunnels] = useState<Funnel[]>([]);
  const [selectedFunnelId, setSelectedFunnelId] = useState<string>('');
  const [labels, setLabels] = useState<Array<{ name: string; color: string }>>([]);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [showLabelFilter, setShowLabelFilter] = useState(false);
  const labelColors = useMemo(
    () => Object.fromEntries(labels.map((label) => [label.name.trim().toLowerCase(), label.color])),
    [labels]
  );
  const lastSnapshotRef = useRef<{ columns: Record<ColumnId, string[]>; contactsById: Record<string, Contact> } | null>(null);
  const navigate = useNavigate();
  const [sendingContactId, setSendingContactId] = useState<string | null>(null);
  const [inboxChoice, setInboxChoice] = useState<{ contact: Contact; inboxes: { externalAccountId: string; name: string }[] } | null>(null);

  // Abre (ou cria) a conversa do contato no Chat CRM. Se ja houve conversa,
  // reabre a MESMA janela; senao inicia uma nova (pedindo a conexao se houver
  // mais de uma sessao WhatsApp).
  const openChatForContact = async (contact: Contact, externalAccountId?: string) => {
    if (sendingContactId) return;
    setSendingContactId(contact.id);
    const loadingToastId = toast.loading(externalAccountId ? 'Verificando número no WhatsApp...' : 'Localizando conversa...');
    try {
      const res = await apiService.resolveConversationForContact(contact.id, externalAccountId);
      const data = res.data;
      if ((data.status === 'existing' || data.status === 'created') && data.conversationId) {
        setInboxChoice(null);
        navigate('/chat-crm', { state: { selectConversationId: data.conversationId } });
      } else if (data.status === 'choose_inbox') {
        setInboxChoice({ contact, inboxes: data.inboxes || [] });
      } else if (data.status === 'no_inbox') {
        toast.error('Nenhuma conexão WhatsApp disponível para iniciar a conversa.');
      } else if (data.status === 'no_phone') {
        toast.error('Este contato não tem um telefone válido.');
      } else if (data.status === 'not_on_whatsapp') {
        toast.error('Este número não está disponível no WhatsApp.');
      } else {
        toast.error('Não foi possível abrir a conversa. Tente novamente.');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao abrir a conversa');
    } finally {
      toast.dismiss(loadingToastId);
      setSendingContactId(null);
    }
  };

  const handleSendMessage = (contact: Contact) => {
    openChatForContact(contact);
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const activeContact = useMemo(() => {
    if (!activeContactId) return null;
    return contactsById[activeContactId] || null;
  }, [activeContactId, contactsById]);

  const rebuildStateFromServer = async (funnelId?: string) => {
    try {
      setLoading(true);
      const data = await apiService.getContactsKanban({ funnelId, label: selectedLabel || undefined });

      const nextCategories = data.categories || [];
      const nextContacts = data.contacts || [];

      const nextContactsById: Record<string, Contact> = {};
      nextContacts.forEach((c) => {
        nextContactsById[c.id] = c;
      });

      const allColumnIds: ColumnId[] = ['none', ...nextCategories.map((c) => c.id)];

      const nextColumns: Record<ColumnId, string[]> = {};
      allColumnIds.forEach((cid) => {
        nextColumns[cid] = [];
      });

      nextContacts.forEach((c) => {
        const colId = buildColumnIdFromCategoriaId(c.categoriaId);
        if (!nextColumns[colId]) nextColumns[colId] = [];
        nextColumns[colId].push(c.id);
      });

      Object.keys(nextColumns).forEach((cid) => {
        nextColumns[cid] = [...nextColumns[cid]];
      });

      setCategories(nextCategories);
      setContactsById(nextContactsById);
      setColumnOrder(allColumnIds);
      setColumns(nextColumns);
      setColumnTotals(data.totals || {});
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao carregar kanban';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreContacts = async (columnId: ColumnId) => {
    const currentIds = columns[columnId] || [];
    const total = columnTotals[columnId] || 0;
    if (loadingMoreColumns[columnId] || currentIds.length >= total) return;

    setLoadingMoreColumns((prev) => ({ ...prev, [columnId]: true }));
    try {
      const data = await apiService.getContactsKanban({
        funnelId: selectedFunnelId || undefined,
        columnId,
        offset: currentIds.length,
        label: selectedLabel || undefined,
      });
      const newContacts = data.contacts || [];
      setContactsById((prev) => {
        const next = { ...prev };
        newContacts.forEach((contact) => { next[contact.id] = contact; });
        return next;
      });
      setColumns((prev) => ({
        ...prev,
        [columnId]: Array.from(new Set([...(prev[columnId] || []), ...newContacts.map((contact) => contact.id)])),
      }));
      setColumnTotals((prev) => ({ ...prev, ...data.totals }));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao carregar mais contatos');
    } finally {
      setLoadingMoreColumns((prev) => ({ ...prev, [columnId]: false }));
    }
  };

  useEffect(() => {
    rebuildStateFromServer(selectedFunnelId || undefined);
  }, [selectedFunnelId, selectedLabel]);

  useEffect(() => {
    const loadFunnels = async () => {
      try {
        const data = await apiService.getFunnels();
        setFunnels(data);
        if (!selectedFunnelId && data.length > 0) {
          const inbound = data.find((funnel) => funnel.isInbound);
          setSelectedFunnelId(inbound?.id || data[0].id);
        }
      } catch (error) {
        console.error('Erro ao carregar funis:', error);
      }
    };
    loadFunnels();
    apiService.getContactLabels().then(setLabels).catch(() => setLabels([]));
  }, []);

  const findColumnIdByContactId = (contactId: string): ColumnId | null => {
    for (const colId of Object.keys(columns)) {
      if (columns[colId]?.includes(contactId)) return colId;
    }
    return null;
  };

  const handleDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id);
    setActiveContactId(id);
    lastSnapshotRef.current = {
      columns: Object.fromEntries(
        (Object.entries(columns) as Array<[ColumnId, string[]]>).map(([k, v]) => [k, [...v]])
      ) as Record<ColumnId, string[]>,
      contactsById: { ...contactsById },
    };
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveContactId(null);

    if (!over) {
      return;
    }

    const activeId = String(active.id);
    const overId = String(over.id);

    const sourceColumnId = findColumnIdByContactId(activeId);
    if (!sourceColumnId) return;

    let destColumnId: ColumnId | null = null;
    let destIndex: number;

    if (overId.startsWith('column:')) {
      destColumnId = overId.replace('column:', '');
      destIndex = (columns[destColumnId] || []).length;
    } else {
      destColumnId = findColumnIdByContactId(overId);
      if (!destColumnId) return;
      destIndex = (columns[destColumnId] || []).indexOf(overId);
      if (destIndex < 0) destIndex = (columns[destColumnId] || []).length;
    }

    if (!destColumnId) return;

    const sourceIds = columns[sourceColumnId] || [];
    const destIds = columns[destColumnId] || [];

    if (sourceColumnId === destColumnId) {
      const oldIndex = sourceIds.indexOf(activeId);
      const newIndex = overId.startsWith('column:') ? sourceIds.length - 1 : destIds.indexOf(overId);
      if (oldIndex < 0 || newIndex < 0) return;

      const reordered = arrayMove(sourceIds, oldIndex, newIndex);
      setColumns((prev) => ({ ...prev, [sourceColumnId]: reordered }));

      try {
        await apiService.moveKanbanContact({
          contactId: activeId,
          toCategoriaId: categoriaIdFromColumnId(destColumnId),
          toIndex: newIndex,
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Erro ao mover contato';
        toast.error(message);
        if (lastSnapshotRef.current) {
          setColumns(lastSnapshotRef.current.columns);
          setContactsById(lastSnapshotRef.current.contactsById);
        }
      }

      return;
    }

    const nextSource = sourceIds.filter((id) => id !== activeId);
    const nextDest = [...destIds];
    const clampedIndex = Math.max(0, Math.min(destIndex, nextDest.length));
    nextDest.splice(clampedIndex, 0, activeId);

    setColumns((prev) => ({
      ...prev,
      [sourceColumnId]: nextSource,
      [destColumnId]: nextDest,
    }));

    setContactsById((prev) => ({
      ...prev,
      [activeId]: { ...prev[activeId], categoriaId: categoriaIdFromColumnId(destColumnId) },
    }));
    setColumnTotals((prev) => ({
      ...prev,
      [sourceColumnId]: Math.max(0, (prev[sourceColumnId] || 0) - 1),
      [destColumnId]: (prev[destColumnId] || 0) + 1,
    }));

    try {
      await apiService.moveKanbanContact({
        contactId: activeId,
        toCategoriaId: categoriaIdFromColumnId(destColumnId),
        toIndex: clampedIndex,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao mover contato';
      toast.error(message);
        if (lastSnapshotRef.current) {
          setColumns(lastSnapshotRef.current.columns);
          setContactsById(lastSnapshotRef.current.contactsById);
        }
        rebuildStateFromServer(selectedFunnelId || undefined);
      }
  };

  const getColumnMeta = (columnId: ColumnId) => {
    if (columnId === 'none') {
      return { title: 'Sem categoria', color: '#94a3b8' };
    }
    const cat = categories.find((c) => c.id === columnId);
    return { title: cat?.nome || 'Categoria', color: cat?.cor || '#94a3b8' };
  };

  return (
    <>
      <Header
        title="Contatos - Kanban"
        subtitle={loading ? 'Carregando...' : 'Arraste e solte para organizar'}
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-0 sm:min-w-[220px]">
              <select
                value={selectedFunnelId}
                onChange={(e) => setSelectedFunnelId(e.target.value)}
                className="input-field text-sm"
              >
                <option value="">Todos os funis</option>
                {funnels.map((funnel) => (
                  <option key={funnel.id} value={funnel.id}>
                    {funnel.nome}{funnel.isInbound ? ' (Inbound)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <button type="button" onClick={() => setShowLabelFilter((open) => !open)} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700">{selectedLabel ? `Etiqueta: ${selectedLabel}` : 'Filtrar por etiqueta'}</button>
            <button
              onClick={() => rebuildStateFromServer(selectedFunnelId || undefined)}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm font-medium transition-colors"
              type="button"
            >
              Recarregar
            </button>
          </div>
        }
      />

      {showLabelFilter && <div className="mx-4 mt-4 max-w-xl rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:mx-6"><ContactLabelSelector value={selectedLabel ? [selectedLabel] : []} onChange={(values) => setSelectedLabel(values[values.length - 1] || '')} label="Filtrar contatos do Kanban por etiqueta" helperText="" /></div>}

      <div className="p-4 sm:p-6">
        {loading && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />
            Preparando o Kanban...
          </div>
        )}
        <div className="overflow-x-auto">
          <div className="flex gap-4 min-w-max">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              {columnOrder.map((colId) => {
                const meta = getColumnMeta(colId);
                return (
                  <KanbanColumn
                    key={colId}
                    title={meta.title}
                    color={meta.color}
                    columnId={colId}
                    contactIds={columns[colId] || []}
                    contactsById={contactsById}
                    onSendMessage={handleSendMessage}
                    total={columnTotals[colId] || 0}
                    loadingMore={!!loadingMoreColumns[colId]}
                    onLoadMore={() => loadMoreContacts(colId)}
                    labelColors={labelColors}
                  />
                );
              })}

              <DragOverlay>
                {activeContact ? (
                  <div className="w-72">
                    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                      <div className="text-sm font-semibold text-gray-900 break-words">{activeContact.nome}</div>
                      <div className="text-xs text-gray-600 mt-1 break-all">{activeContact.telefone}</div>
                      <div className="text-[11px] text-gray-400 mt-2 break-all">{activeContact.id}</div>
                    </div>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>
        </div>
      </div>

      {/* Modal: escolher conexão WhatsApp para iniciar a conversa */}
      {inboxChoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setInboxChoice(null)}>
          <div className="w-full max-w-sm max-h-[90dvh] overflow-y-auto rounded-xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900">Iniciar conversa</h3>
            <p className="mt-1 text-sm text-gray-500">
              Escolha por qual conexão WhatsApp iniciar a conversa com <span className="font-medium text-gray-700">{inboxChoice.contact.nome}</span>.
            </p>
            <div className="mt-4 space-y-2">
              {inboxChoice.inboxes.map((inbox) => (
                <button
                  key={inbox.externalAccountId}
                  type="button"
                  disabled={!!sendingContactId}
                  onClick={() => openChatForContact(inboxChoice.contact, inbox.externalAccountId)}
                  className="flex w-full items-center gap-3 rounded-lg border border-gray-200 px-3 py-2.5 text-left text-sm text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347"/></svg>
                  </span>
                  <span className="truncate">{inbox.name}</span>
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setInboxChoice(null)}
              className="mt-4 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </>
  );
}
